import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { CheckCircle } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import {
  fetchAssessmentQuestions,
  listAssessmentAttempts,
  submitAssessment,
  type AssessmentType,
  type QuestionItem,
} from '../../lib/apiClient';
import {
  formatAnswers,
  isComplete,
  newSubmissionId,
  savePendingAssessment,
} from '../../lib/assessment/submission';
import { ASSESSMENT_LABELS, routes } from '../../lib/assessment/routes';
// Shared with the dashboard (U1b). This was a private `const FREE_LIMIT = 3`
// here until a second surface needed the same number; two copies of a limit is
// how a dashboard comes to promise an attempt this runner then refuses.
import { FREE_LIMIT, completedCount } from '../../lib/assessment/attempts';
import { usePageMeta } from '../../lib/usePageMeta';

/** Production's per-runner page meta. */
const RUNNER_META: Record<string, { title: string; description?: string }> = {
  bottleneck: {
    title: 'Take the High Performer Bottleneck Assessment | High Performer OS',
    description:
      'Take the 7-minute assessment to discover your High Performer Type, top performance bottlenecks, and one immediate action to improve execution.',
  },
  work_fit: {
    title: 'Take the High Performer Work Fit Assessment | High Performer OS',
    description:
      'Take the 50-question Work Fit Assessment to discover the environments, manager styles, and cultures that fit how your performance style actually operates.',
  },
  perspective: {
    title: 'Take the Perspective Assessment | High Performer OS',
    description:
      'Take the 50-question Perspective Assessment to discover the interpretive patterns that are shaping your decisions, confidence, and performance.',
  },
};
// The Likert options stay native buttons: they carry aria-pressed toggle
// semantics the Button primitive does not model, and Appendix B item 4 directs
// that this markup be preserved. They take the section E.6 option classes
// instead, including the mandatory focus ring.
import { Button } from '../ui/button';

const LIKERT = [
  { value: 1, label: 'Strongly Disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Agree' },
  { value: 5, label: 'Strongly Agree' },
];

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'limit-reached' }
  | { phase: 'ready'; questions: QuestionItem[] };

export function AssessmentRunner({ type }: { type: AssessmentType }) {
  const { status, session } = useAuth();
  const [, navigate] = useLocation();
  const token = session?.access_token ?? null;

  usePageMeta({ ...(RUNNER_META[type] ?? { title: 'Assessment | High Performer OS' }), noindex: true });

  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submissionId] = useState(() => newSubmissionId());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Deliver questions (public) and, for a signed-in user, run the eligibility
  // preflight so the 3-completed-attempt limit shows before answering 50 questions.
  useEffect(() => {
    let cancelled = false;
    setLoad({ phase: 'loading' });
    setAnswers({});

    void (async () => {
      const questionsResult = await fetchAssessmentQuestions(type);
      if (cancelled) return;
      if (questionsResult.state !== 'ok') {
        setLoad({ phase: 'error' });
        return;
      }

      if (token) {
        const attempts = await listAssessmentAttempts(token, type);
        if (cancelled) return;
        if (attempts.state === 'ok') {
          if (completedCount(attempts.attempts, type) >= FREE_LIMIT) {
            setLoad({ phase: 'limit-reached' });
            return;
          }
        }
      }

      setLoad({ phase: 'ready', questions: questionsResult.questions });
    })();

    return () => {
      cancelled = true;
    };
  }, [type, token]);

  if (load.phase === 'loading') {
    return <p className="text-muted-foreground">Loading assessment…</p>;
  }
  if (load.phase === 'error') {
    return (
      <p className="text-destructive">This assessment could not be loaded. Please try again.</p>
    );
  }
  if (load.phase === 'limit-reached') {
    return (
      <section
        className="mx-auto flex w-full max-w-md flex-col items-center gap-6 text-center"
        data-testid="assessment-limit"
      >
        <span className="w-fit rounded-full bg-secondary p-4">
          <CheckCircle className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        </span>
        <div>
          <h1 className="text-2xl font-bold">Free Assessments Used</h1>
          <p className="mt-2 text-muted-foreground">
            You have used all {FREE_LIMIT} free {ASSESSMENT_LABELS[type]}s. Your results and full
            reports are available on the dashboard.
          </p>
        </div>
        <Link
          href={routes.dashboard}
          data-testid="assessment-limit-dashboard"
          className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <Button className="gap-2" tabIndex={-1}>
            Back to Dashboard
          </Button>
        </Link>
      </section>
    );
  }

  const questions = load.questions;
  const total = questions.length;
  const complete = isComplete(answers, total);
  const answeredCount = Object.keys(answers).length;

  const onSubmit = async (): Promise<void> => {
    if (!complete || submitting) return;
    setError(null);
    const formatted = formatAnswers(answers);

    // Signed-out: stash the completed set (with the same submissionId) and route to
    // the sign-up interstitial; the /welcome claim submits it after authentication.
    if (status !== 'signed-in' || !token) {
      savePendingAssessment(window.sessionStorage, { type, submissionId, answers: formatted });
      navigate(routes.resultsReady);
      return;
    }

    setSubmitting(true);
    const result = await submitAssessment(token, type, submissionId, formatted);
    setSubmitting(false);

    if (result.state === 'ok') {
      navigate(routes.result(result.resultId));
      return;
    }
    if (result.state === 'limit-reached') {
      setLoad({ phase: 'limit-reached' });
      return;
    }
    if (result.state === 'invalid') {
      // Production's wording for an incomplete submission.
      setError('Please answer all questions.');
      return;
    }
    setError('Failed to submit assessment.');
  };

  return (
    <section className="flex flex-col gap-6">
      {/*
        Production's progress header: the label on the left, the count on the
        right, a bar beneath. V2 printed "N / total answered" in one line and no
        bar, so there was nothing to read at a glance halfway through fifty
        questions.
      */}
      <div className="sticky top-0 z-10 bg-background/95 py-3 backdrop-blur">
        {/* Production's eyebrow above the progress bar. */}
        <p
          className="mb-2 text-xs font-bold uppercase tracking-widest text-primary"
          data-testid="assessment-eyebrow"
        >
          High Performer OS Assessment
        </p>
        <div className="mb-2 flex justify-between text-sm text-muted-foreground">
          <span>Assessment Progress</span>
          <span data-testid="progress">
            {answeredCount} / {total}
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-primary transition-all"
            data-testid="progress-bar"
            style={{ width: `${total > 0 ? Math.round((answeredCount / total) * 100) : 0}%` }}
          />
        </div>
        {/* Production prints this on the Bottleneck runner and only there. */}
        {type === 'bottleneck' ? (
          <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="assessment-duration">
            7 minutes · 50 questions
          </p>
        ) : null}
      </div>

      <ol className="flex flex-col gap-8">
        {questions.map((question) => (
          <li key={question.id} className="flex flex-col gap-2">
            <p className="font-medium">
              {question.order}. {question.prompt}
            </p>
            {/*
              Production lays the scale out as five equal cards across, each
              showing its number above the wording, and stacks them on a narrow
              screen. V2 showed a wrapping row of text-only pills with no
              numbers at all — even though the page, the marketing copy and the
              FAQ all describe "a rating scale of 1 to 5". The numbers are what
              make it a scale, so they are restored here.

              The control stays a native button with `aria-pressed` per
              Appendix B item 4. Production uses a radiogroup; that difference
              is semantic rather than visible and is left to the shell review
              rather than changed here.
            */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-5">
              {LIKERT.map((option) => {
                const selected = answers[question.id] === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={selected}
                    className={`flex select-none items-center gap-3 rounded-lg border p-3 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:flex-col sm:justify-center sm:gap-2 ${
                      selected
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/40'
                    }`}
                    onClick={() =>
                      setAnswers((prev) => ({ ...prev, [question.id]: option.value }))
                    }
                  >
                    <span className="flex items-center gap-1.5 sm:flex-col sm:text-center">
                      <span
                        className={`text-sm font-bold ${
                          selected ? 'text-primary' : 'text-foreground'
                        }`}
                      >
                        {option.value}
                      </span>
                      <span className="text-xs text-muted-foreground">{option.label}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </li>
        ))}
      </ol>

      {error ? <p className="text-destructive">{error}</p> : null}

      <Button
        type="button"
        disabled={!complete || submitting}
        onClick={() => {
          void onSubmit();
        }}
        className="self-start"
        data-testid="submit"
      >
        {submitting ? 'Submitting…' : 'See my result'}
      </Button>
      {!complete ? (
        <p className="text-sm text-muted-foreground" data-testid="assessment-incomplete">
          Please answer all questions.
        </p>
      ) : null}
    </section>
  );
}
